import { Fragment, memo } from 'react';

import { isEmpty, isNonNullish } from 'remeda';
import useSWR from 'swr';

import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';

import { SearchType } from '../../constants';
import { FileRow } from '../fileSystemTable/fileRow/FileRow';
import { FolderRow } from '../fileSystemTable/folderRow/FolderRow';

import { fileSystemService } from 'v2/api/fileSystemService';
import { EmptyPlaceholder } from 'v2/components/emptyPlaceholder/EmptyPlaceholder';
import { Loader } from 'v2/components/loader/Loader';

interface FileSystemSearchResultsProps {
  search: string;
  type: SearchType;
}

export const FileSystemSearchResults = memo<FileSystemSearchResultsProps>(({ search, type }) => {
  const {
    data: files,
    isLoading: isLoadingFiles,
    mutate: mutateFiles,
  } = useSWR(type === SearchType.File && ['getFilesBySearch', search], fileSystemService.getFilesBySearch);

  const {
    data: folders,
    isLoading: isLoadingFolders,
    mutate: mutateFolders,
  } = useSWR(type === SearchType.Folder && ['getFoldersBySearch', search], fileSystemService.getFoldersBySearch);

  if (isLoadingFiles || isLoadingFolders) {
    return <Loader />;
  }

  return (
    <TableContainer>
      <Table size="small">
        <TableBody>
          {isNonNullish(folders) && (
            <Fragment>
              {isEmpty(folders) ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <EmptyPlaceholder />
                  </TableCell>
                </TableRow>
              ) : (
                folders.map((folder) => (
                  <FolderRow key={folder.id} folder={folder} depth={0} onRevalidate={mutateFolders} />
                ))
              )}
            </Fragment>
          )}

          {isNonNullish(files) && (
            <Fragment>
              {isEmpty(files) ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <EmptyPlaceholder />
                  </TableCell>
                </TableRow>
              ) : (
                files.map((file) => <FileRow key={file.id} file={file} depth={0} onRevalidate={mutateFiles} />)
              )}
            </Fragment>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
});

FileSystemSearchResults.displayName = 'FileSystemSearchResults';
